import { useEffect, useState } from 'react';
import { PageHeader } from '../components/Layout';
import { Badge, Button, Card, ErrorNote, Field, Input, Loading, Modal, Table, Td, Th } from '../components/ui';
import {
  api,
  type GmailBackfillState,
  type GmailIgnoredDomain,
  type GmailSuggestedContact,
  type GmailTimelineSyncResult,
  type Prospect,
} from '../lib/api';
import { dateTime } from '../lib/format';
import { useAction, useApi, useDebounced } from '../lib/hooks';

/**
 * The suggestions tray and mailbox backfill from CRM_ROADMAP.md phase 3.
 * Its own page rather than a card buried in Settings — admins check this
 * regularly to keep the CRM populated, unlike the rest of Settings, which is
 * mostly configure-once. Admin-only, same as the underlying /api/gmail/*
 * routes: these rows are raw sender addresses read straight out of the
 * connected mailbox, not yet reviewed for whether they belong in the CRM.
 */
export function GmailContacts() {
  const suggestions = useApi<{ suggestions: GmailSuggestedContact[] }>('/api/gmail/suggested-contacts');
  const [syncResult, setSyncResult] = useState<GmailTimelineSyncResult | null>(null);
  const [addingContact, setAddingContact] = useState<GmailSuggestedContact | null>(null);
  const [linkingContact, setLinkingContact] = useState<GmailSuggestedContact | null>(null);
  const domains = useApi<{ domains: GmailIgnoredDomain[] }>('/api/gmail/ignored-domains');

  const syncNow = useAction(async () => {
    const { result } = await api.post<{ result: GmailTimelineSyncResult }>('/api/gmail/sync', {});
    setSyncResult(result);
    suggestions.reload();
  });

  const ignore = useAction(async (id: string) => {
    await api.post(`/api/gmail/suggested-contacts/${id}/ignore`, {});
    suggestions.reload();
  });

  const ignoreDomain = useAction(async (domain: string) => {
    await api.post('/api/gmail/ignored-domains', { domain });
    suggestions.reload();
    domains.reload();
  });

  const rows = suggestions.data?.suggestions ?? [];

  return (
    <>
      <PageHeader
        title="Gmail contacts"
        description="People who've emailed the connected mailbox but aren't tracked prospects yet."
        helpHref="gmail-contacts"
        actions={
          <Button loading={syncNow.pending} onClick={() => void syncNow.run()}>
            Sync now
          </Button>
        }
      />

      <Card>
        <p className="text-sm text-ink-600">
          Accepting one creates a prospect; ignoring is permanent so the address won't resurface.
          Newsletter and no-reply senders, and mail between two addresses on the mailbox's own
          domain, are filtered out automatically and never reach this list.
        </p>

        {syncNow.error && <div className="mt-3"><ErrorNote message={syncNow.error} /></div>}
        {syncResult && (
          <p className="mt-2 text-xs text-ink-500">
            Checked {syncResult.messagesChecked} messages · {syncResult.eventsAdded} timeline events ·{' '}
            {syncResult.suggestionsAdded} new suggestions
            {syncResult.errors.length > 0 && ` · ${syncResult.errors.length} errors`}
          </p>
        )}
        {ignore.error && <div className="mt-2"><ErrorNote message={ignore.error} /></div>}
        {ignoreDomain.error && <div className="mt-2"><ErrorNote message={ignoreDomain.error} /></div>}

        {suggestions.loading ? (
          <div className="mt-4"><Loading /></div>
        ) : rows.length === 0 ? (
          <p className="mt-4 text-sm text-ink-500">Nothing pending. New senders show up here after a sync.</p>
        ) : (
          <div className="mt-4">
            <Table>
              <thead>
                <tr>
                  <Th>Contact</Th>
                  <Th>Last seen</Th>
                  <Th>Messages</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <Td>
                      {row.display_name && row.display_name !== row.email ? (
                        <>
                          <div className="font-medium">{row.display_name}</div>
                          <div className="text-xs text-ink-500">{row.email}</div>
                        </>
                      ) : (
                        <div className="font-medium">{row.email}</div>
                      )}
                    </Td>
                    <Td className="text-ink-500">{dateTime(row.last_seen_at)}</Td>
                    <Td className="tabular-nums">{row.thread_count}</Td>
                    <Td>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="primary" onClick={() => setAddingContact(row)}>
                          Add as prospect
                        </Button>
                        <Button size="sm" onClick={() => setLinkingContact(row)}>
                          Link to contact
                        </Button>
                        <Button size="sm" loading={ignore.pending} onClick={() => void ignore.run(row.id)}>
                          Ignore
                        </Button>
                        {row.domain && (
                          <Button
                            size="sm"
                            loading={ignoreDomain.pending}
                            onClick={() => void ignoreDomain.run(row.domain as string)}
                          >
                            Ignore {row.domain}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Card title="Ignored domains" className="mt-4">
        <IgnoredDomains
          domains={domains.data?.domains ?? []}
          onChanged={() => {
            domains.reload();
            suggestions.reload();
          }}
        />
      </Card>

      <Card title="Mailbox backfill" className="mt-4">
        <MailboxBackfill onProgress={suggestions.reload} />
      </Card>

      <LinkSuggestedContactModal
        suggestion={linkingContact}
        onClose={() => setLinkingContact(null)}
        onDone={() => {
          setLinkingContact(null);
          suggestions.reload();
        }}
      />

      <AddSuggestedContactModal
        suggestion={addingContact}
        onClose={() => setAddingContact(null)}
        onDone={() => {
          setAddingContact(null);
          suggestions.reload();
        }}
      />
    </>
  );
}

/**
 * The domain ignore list (backlog #32). It only filters this tray: a sender who
 * is already a contact, or an alias of one, still reaches their timeline.
 */
function IgnoredDomains({ domains, onChanged }: { domains: GmailIgnoredDomain[]; onChanged: () => void }) {
  const [domain, setDomain] = useState('');
  const add = useAction(async () => {
    await api.post('/api/gmail/ignored-domains', { domain });
    setDomain('');
    onChanged();
  });
  const remove = useAction(async (name: string) => {
    await api.del(`/api/gmail/ignored-domains/${encodeURIComponent(name)}`);
    onChanged();
  });

  return (
    <div>
      <p className="text-sm text-ink-600">
        Senders from these domains, and their subdomains, never reach the suggestions list. It does not
        hide anyone who is already a contact. Adding a domain also clears what is pending from it.
      </p>
      {domains.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {domains.map((d) => (
            <li key={d.domain} className="flex items-center gap-1.5 rounded-md bg-ink-50 px-2.5 py-1 text-sm ring-1 ring-inset ring-ink-200">
              {d.domain}
              <button
                type="button"
                aria-label={`Stop ignoring ${d.domain}`}
                className="text-ink-400 hover:text-ink-700"
                onClick={() => void remove.run(d.domain)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {(add.error || remove.error) && <div className="mt-2"><ErrorNote message={add.error ?? remove.error ?? ''} /></div>}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Field label="Ignore a domain">
          <Input value={domain} placeholder="example.com" onChange={(event) => setDomain(event.target.value)} />
        </Field>
        <Button size="sm" variant="primary" disabled={!domain.trim()} loading={add.pending} onClick={() => void add.run()}>
          Ignore domain
        </Button>
      </div>
    </div>
  );
}

/**
 * Files a suggestion under a contact that already exists: the address becomes
 * an alias, so mail to or from it reaches that person's timeline.
 */
function LinkSuggestedContactModal({
  suggestion,
  onClose,
  onDone,
}: {
  suggestion: GmailSuggestedContact | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 250);
  const results = useApi<{ prospects: Prospect[] }>(
    suggestion && debounced.trim().length >= 2
      ? `/api/prospects?search=${encodeURIComponent(debounced.trim())}&limit=8`
      : null,
  );

  useEffect(() => {
    if (suggestion) setSearch('');
  }, [suggestion]);

  const link = useAction(async (prospectId: string) => {
    if (!suggestion) return;
    await api.post(`/api/gmail/suggested-contacts/${suggestion.id}/link`, { prospect_id: prospectId });
    onDone();
  });

  return (
    <Modal open={Boolean(suggestion)} onClose={onClose} title="Link to an existing contact">
      {suggestion && (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">
            {suggestion.email} becomes another address for the contact you pick. Mail to or from it lands on
            their timeline from the next sync; older mail arrives with a mailbox backfill.
          </p>
          <Field label="Find the contact">
            <Input value={search} placeholder="Name, company or email" onChange={(event) => setSearch(event.target.value)} />
          </Field>
          {link.error && <ErrorNote message={link.error} />}
          <ul className="divide-y divide-ink-100">
            {(results.data?.prospects ?? []).map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">
                    {`${p.first_name} ${p.last_name}`.trim() || p.email}
                  </p>
                  <p className="truncate text-xs text-ink-500">
                    {[p.email, p.company_name].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button size="sm" loading={link.pending} onClick={() => void link.run(p.id)}>
                  Link
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end pt-1">
            <Button onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** A year ago, as a `date` input default — far enough back to be useful, not so far it's arbitrary. */
function oneYearAgoInputValue(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().slice(0, 10);
}

const BACKFILL_STATUS_LABEL: Record<GmailBackfillState['status'], string> = {
  idle: 'Not started',
  running: 'Running',
  completed: 'Completed',
  error: 'Error',
};

/**
 * The one-time "import mailbox history" action from CRM_ROADMAP.md phase 3.
 * The regular sync only looks back 30 days on its own first run; this walks
 * further back on request. It runs a bounded page per scheduler tick rather
 * than all at once, so there's no progress bar here — just a status line to
 * refresh.
 */
function MailboxBackfill({ onProgress }: { onProgress: () => void }) {
  const status = useApi<{ backfill: GmailBackfillState }>('/api/gmail/backfill');
  const [since, setSince] = useState(oneYearAgoInputValue());

  const start = useAction(async () => {
    await api.post('/api/gmail/backfill', { since: new Date(since).toISOString() });
    status.reload();
  });

  const backfill = status.data?.backfill;
  const isRunning = backfill?.status === 'running';

  return (
    <div>
      <p className="text-sm text-ink-600">
        A one-time import further back than the regular sync's 30-day first look — useful right
        after connecting Gmail to pull in existing history. Runs a page at a time in the
        background; refresh to check progress.
      </p>

      {backfill && backfill.status !== 'idle' && (
        <div className="mt-3 rounded-lg border border-ink-200 bg-ink-50 p-3 text-xs text-ink-700">
          <p>
            <Badge tone={backfill.status === 'error' ? 'red' : backfill.status === 'completed' ? 'green' : 'blue'}>
              {BACKFILL_STATUS_LABEL[backfill.status]}
            </Badge>{' '}
            since {backfill.since ? dateTime(backfill.since) : '—'}
          </p>
          <p className="mt-1">
            Checked {backfill.messagesChecked} messages · {backfill.eventsAdded} timeline events ·{' '}
            {backfill.suggestionsAdded} new suggestions
            {backfill.errors.length > 0 && ` · ${backfill.errors.length} errors`}
          </p>
        </div>
      )}

      {start.error && <div className="mt-2"><ErrorNote message={start.error} /></div>}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Field label="Import mail since">
          <Input type="date" value={since} onChange={(event) => setSince(event.target.value)} disabled={isRunning} />
        </Field>
        <Button size="sm" variant="primary" disabled={isRunning} loading={start.pending} onClick={() => void start.run()}>
          {isRunning ? 'Running…' : 'Start backfill'}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            status.reload();
            onProgress();
          }}
        >
          Refresh status
        </Button>
      </div>
    </div>
  );
}

/**
 * Lets the operator confirm or correct the guessed name and add a company
 * before the suggestion becomes a prospect — the sync only ever captured an
 * email address and whatever display name Gmail sent along with it.
 */
function AddSuggestedContactModal({
  suggestion,
  onClose,
  onDone,
}: {
  suggestion: GmailSuggestedContact | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  useEffect(() => {
    if (!suggestion) return;
    const [guessedFirst = '', ...guessedLastParts] = suggestion.display_name.split(' ').filter(Boolean);
    setFirstName(guessedFirst);
    setLastName(guessedLastParts.join(' '));
    setCompanyName(suggestion.domain ?? '');
    setJobTitle('');
  }, [suggestion]);

  const add = useAction(async () => {
    if (!suggestion) return;
    await api.post(`/api/gmail/suggested-contacts/${suggestion.id}/accept`, {
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      job_title: jobTitle,
    });
    onDone();
  });

  return (
    <Modal open={Boolean(suggestion)} onClose={onClose} title="Add as prospect">
      {suggestion && (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">{suggestion.email}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
            </Field>
            <Field label="Last name">
              <Input value={lastName} onChange={(event) => setLastName(event.target.value)} />
            </Field>
          </div>
          <Field label="Company">
            <Input value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
          </Field>
          <Field label="Job title">
            <Input value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} />
          </Field>

          {add.error && <ErrorNote message={add.error} />}

          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={add.pending} onClick={() => void add.run()}>
              Add prospect
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
